import React from 'react';
import { Chart } from 'react-stockcharts';
import { XAxis, YAxis } from 'react-stockcharts/lib/axes';
import { StraightLine } from 'react-stockcharts/lib/series';
import {
  ChartCanvas,
  CrossHairCursor,
  CurrentCoordinate,
  LineSeries,
  MouseCoordinateX,
} from '../../../common/chart';
import moment from 'moment';
import { getStandardDeviation } from '../../../helpers/Common';
import Const from './Const';
import { formatTextFloat } from './../../../helpers/Text';
import { connect } from 'react-redux';
import HoverTooltip from '../../../common/chart/lib/tooltip/HoverTooltip';
import {
  getRoundedTickValues,
  tickIntervalFunction,
  yExtentsCharts,
} from './../../../helpers/Chart';
import { THEMES } from './../../../../configs/LayoutConfig';
import { ChartConfig, COMMON_SETTINGS } from './ChartConfig';

// const COMMON_SETTINGS = {
//   MARGIN: { left: 40, right: 40, top: 0, bottom: 30 },
//   TEXT_SM_SIZE: 10,
//   TEXT_MD_SIZE: 12,
// };

// const SETTINGS_DARK = {
//   TEXT_COLOR: '#ffffff',
//   CHART_INDEX: {
//     COLOR: '#47c3ff',
//   },
//   CHART_RATE: {
//     COLOR: '#facc5c',
//   },
//   COORDINATE_COLOR_1: 'rgba(87, 249, 214, 0.15)', // blue line
//   COORDINATE_COLOR_2: 'rgba(255,200,89, 0.15)', // yellow line
//   ...COMMON_SETTINGS,
// };

// const SETTINGS_LIGHT = {
//   TEXT_COLOR: '#1f2023',
//   CHART_INDEX: {
//     COLOR: '#2c73e8',
//   },
//   CHART_RATE: {
//     COLOR: '#f8bc06',
//   },
//   COORDINATE_COLOR_1: 'rgba(44,115,232, 0.15)', // blue line
//   COORDINATE_COLOR_2: 'rgba(255,200,89, 0.15)', // yellow line
//   ...COMMON_SETTINGS,
// };

// const SETTINGS_CLASSIC = {
//   TEXT_COLOR: '#ffffff',
//   CHART_INDEX: {
//     COLOR: '#47c3ff',
//   },
//   CHART_RATE: {
//     COLOR: '#facc5c',
//   },
//   COORDINATE_COLOR_1: 'rgba(87, 249, 214, 0.15)', // blue line
//   COORDINATE_COLOR_2: 'rgba(255,200,89, 0.15)', // yellow line
//   ...COMMON_SETTINGS,
// };

class ChartContent extends React.Component {
  tooltipContent = (codeName, rateTitle, rateKey) => {
    const { activeLines } = this.props;
    return ({ currentItem, xAccessor }) => {
      const baseData = [
        {
          label: codeName + ': ',
          value: formatTextFloat(currentItem.value),
          color: '#2a78d1',
        },
        {
          label: rateTitle + ': ',
          value: formatTextFloat(currentItem[rateKey]),
          color: '#ffaa00',
        },
      ];
      const extraData = [];
      this.getLineValues().forEach(item => {
        if (activeLines[item.lineId]) {
          extraData.push({
            label: item.name + ': ',
            value: formatTextFloat(item.value),
            color: item.color,
          });
        }
      });

      return {
        y: baseData.concat(extraData),
      };
    };
  };

  getLineValues = () => {
    const { rate, data } = this.props;
    if (!data.length) {
      return [];
    }
    const arrayOfValues = data.map(item => item[rate]);
    const avg =
      arrayOfValues.reduce((total, val) => total + val, 0) /
      arrayOfValues.length;
    const std = getStandardDeviation(arrayOfValues);

    return [
      {
        color: '#1d97ef',
        name: '+2 StDv',
        value: avg + 2 * std,
        lineId: 2,
      },
      {
        color: '#009d5b',
        name: '+1 StDv',
        value: avg + std,
        lineId: 1,
      },
      {
        color: '#f88634',
        name: '-1 StDv',
        value: avg - std,
        lineId: -1,
      },
      {
        color: '#eb505a',
        value: avg - 2 * std,
        name: '-2 StDv',
        lineId: -2,
      },
    ];
  };

  getRightYExtends = () => {
    const { rate, data } = this.props;
    const { activeLines } = this.props;
    const arrayOfValues = data.map(item => item[rate]);
    const avg =
      arrayOfValues.reduce((total, val) => total + val, 0) /
      arrayOfValues.length;
    const std = getStandardDeviation(arrayOfValues);
    let max = Math.max(...arrayOfValues);
    let min = Math.min(...arrayOfValues);
    if (activeLines[1]) {
      max = Math.max(max, avg + 1 * std);
    }
    if (activeLines[2]) {
      max = Math.max(max, avg + 2 * std);
    }
    if (activeLines[-2]) {
      min = Math.min(min, avg - 2 * std);
    }
    if (activeLines[-1]) {
      min = Math.min(min, avg - 1 * std);
    }

    return [min, max];
  };

  render() {
    const {
      width,
      height,
      ratio,
      data,
      rate,
      codeName,
      theme,
      activeLines,
    } = this.props;
    const SETTINGS = ChartConfig[theme];
    const gridWidth = width - SETTINGS.MARGIN.left - SETTINGS.MARGIN.right;

    const chartHeight = height - 50;
    const [min, max] = yExtentsCharts(data, ['value'], 4, false, false);
    const tickValues = getRoundedTickValues([min, max], 4);
    const yExtends = [tickValues[0], tickValues[tickValues.length - 1]];

    return (
      <ChartCanvas
        width={width}
        height={height}
        data={data}
        ratio={ratio}
        margin={SETTINGS.MARGIN}
      >
        <Chart
          id={0}
          yExtents={yExtends}
          height={chartHeight}
          origin={(w, h) => [0, h - chartHeight]}
        >
          <YAxis
            axisAt="left"
            orient="left"
            tickInterval={4}
            tickValues={tickValues}
            tickStroke="#555555"
            tickStrokeOpacity={0.5}
            showDomain={false}
            zoomEnabled={false}
            outerTickSize={0}
            edgeClip={true}
            showTicks={true}
            showTickLabel={false}
            innerTickSize={-1 * gridWidth}
            tickIntervalFunction={tickIntervalFunction}
          />
          <YAxis
            axisAt="left"
            orient="left"
            tickInterval={4}
            tickStroke={SETTINGS.TEXT_COLOR}
            showDomain={false}
            zoomEnabled={false}
            outerTickSize={0}
            edgeClip={true}
            showTicks={true}
            innerTickSize={0}
            tickIntervalFunction={tickIntervalFunction}
          />

          <LineSeries
            yAccessor={d => d.value}
            stroke={SETTINGS.CHART_INDEX.COLOR}
          />

          <XAxis
            tickPadding={10}
            axisAt="bottom"
            orient="bottom"
            opacity={0}
            innerTickSize={0}
            tickStroke={SETTINGS.TEXT_COLOR}
            ticks={2}
            zoomEnabled={false}
            tickFormat={d => {
              const tradingDate = moment(data[d].tradingDate);
              return tradingDate.format('MM/DD/YYYY').toString();
            }}
          />
          <CurrentCoordinate
            yAccessor={d => d.value}
            color={SETTINGS.COORDINATE_COLOR_1}
          />
          <HoverTooltip
            bgOpacity={0.1}
            fill={'#ffffff'}
            stroke={'#555555'}
            opacity={1}
            yAccessor={d => d.value}
            fontSize={14}
            tooltipContent={this.tooltipContent(
              codeName,
              Const.listRateByKey[rate].name,
              rate,
            )}
            tooltipCanvas={(_ref5, content, ctx) => {
              const X = 10;
              const Y = 7;
              const LINE_SPACE = 3;

              var fontFamily = _ref5.fontFamily,
                fontSize = _ref5.fontSize;
              var startY = Y;

              for (var i = 0; i < content.y.length; i++) {
                const y = content.y[i];
                ctx.font = 'bold ' + fontSize + 'px ' + fontFamily;
                const textY = startY + fontSize * (i + 1) + LINE_SPACE * i;
                ctx.fillStyle = y.color;
                ctx.fillText(y.label, X, textY);

                ctx.font = 'bold ' + fontSize + 'px ' + fontFamily;
                let startXText = X + ctx.measureText(y.label).width;
                ctx.fillStyle = '#2e2e2e';
                ctx.fillText(y.value, startXText, textY);
              }
            }}
          />
        </Chart>
        <Chart
          id={1}
          yExtents={this.getRightYExtends()}
          height={chartHeight}
          origin={(w, h) => [0, h - chartHeight]}
        >
          {this.getLineValues().map(item => {
            if (!activeLines[item.lineId]) {
              return null;
            }
            return (
              <StraightLine
                strokeWidth={2}
                yValue={item.value}
                stroke={item.color}
                key={item.color}
              />
            );
          })}

          <LineSeries
            yAccessor={d => d[rate]}
            stroke={SETTINGS.CHART_RATE.COLOR}
          />

          <YAxis
            axisAt="right"
            orient="right"
            tickInterval={4}
            tickStroke={SETTINGS.TEXT_COLOR}
            opacity={0}
            zoomEnabled={false}
            innerTickSize={0}
            tickIntervalFunction={tickIntervalFunction}
          />
          <CurrentCoordinate
            yAccessor={d => d[rate]}
            color={SETTINGS.COORDINATE_COLOR_2}
          />
          <MouseCoordinateX timeFormat="%m/%d/%Y" />
        </Chart>
        <CrossHairCursor />
      </ChartCanvas>
    );
  }
}

const mapStateToProps = state => ({
  i18n: state.i18n,
  theme: state.theme,
});

export default connect(mapStateToProps)(ChartContent);
